import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { isStaffOnlyHost } from './LandingGate';
import { useSiteSettings } from '../context/SettingsContext';

// Shows a floating "Install app" prompt — only on staff-only hosts (employee portal).
// Captures the browser's beforeinstallprompt event and gives the user a button to install.
// Customer-facing custom domain visitors never see this.
export default function InstallPrompt() {
  const { settings } = useSiteSettings();
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem('tai_install_dismissed') === '1'; } catch { return false; }
  });
  const [installed, setInstalled] = useState(false);

  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const staffOnly = isStaffOnlyHost(settings?.staff_only_domains, host);

  useEffect(() => {
    if (!staffOnly) return undefined;
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [staffOnly]);

  if (!staffOnly || installed || dismissed || !deferred) return null;

  const onInstall = async () => {
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === 'accepted') setInstalled(true);
      setDeferred(null);
    } catch {
      // user dismissed native prompt — keep banner around in case they change their mind
    }
  };

  const onClose = () => {
    setDismissed(true);
    try { localStorage.setItem('tai_install_dismissed', '1'); } catch { /* noop */ }
  };

  return (
    <div className="fixed bottom-5 right-5 z-50 max-w-sm bg-black text-white rounded-2xl shadow-2xl border border-white/10 p-4 pr-3 flex items-start gap-3 animate-in fade-in slide-in-from-bottom-4" data-testid="install-prompt">
      <div className="w-10 h-10 rounded-xl bg-[#e11d2c] grid place-items-center shrink-0">
        <Download size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-[15px] font-bold leading-tight">Install the Staff app</div>
        <p className="text-[12px] text-white/65 mt-0.5 leading-snug">Quick access to inventory, leads, and analytics from your home screen.</p>
        <button onClick={onInstall} data-testid="install-app-btn" className="mt-3 bg-[#e11d2c] hover:bg-[#c01526] text-white text-[12px] font-bold tracking-wider uppercase px-3.5 h-8 rounded-full transition-colors">
          Install
        </button>
      </div>
      <button onClick={onClose} aria-label="Dismiss" className="text-white/55 hover:text-white shrink-0 mt-0.5">
        <X size={16} />
      </button>
    </div>
  );
}
